// Modern Cart Enhanced JavaScript

class ModernCart {
  constructor() {
    this.init();
  }

  init() {
    this.setupQuantityControls();
    this.setupCartUpdates();
    this.setupRemoveButtons();
    this.setupLoadingStates();
    this.setupAnimations();
    this.setupAccessibility();
  }

  // Quantity Controls
  setupQuantityControls() {
    document.querySelectorAll('.quantity-btn').forEach(button => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        this.handleQuantityChange(button);
      });
    });

    document.querySelectorAll('.quantity-input').forEach(input => {
      input.addEventListener('change', (e) => {
        this.validateQuantity(input);
        this.updateCart();
      });

      input.addEventListener('blur', (e) => {
        this.validateQuantity(input);
      });
    });
  }

  handleQuantityChange(button) {
    const input = button.parentNode.querySelector('.quantity-input');
    const isIncrement = button.getAttribute('name') === 'plus';
    const currentValue = parseInt(input.value) || 0;
    const min = parseInt(input.getAttribute('min')) || 1;
    const max = parseInt(input.getAttribute('max')) || 999;
    const step = parseInt(input.getAttribute('step')) || 1;

    let newValue;
    if (isIncrement) {
      newValue = Math.min(currentValue + step, max);
    } else {
      newValue = Math.max(currentValue - step, min);
    }

    if (newValue !== currentValue) {
      input.value = newValue;
      this.animateQuantityChange(input);
      this.updateCart();
    }
  }

  validateQuantity(input) {
    const value = parseInt(input.value) || 0;
    const min = parseInt(input.getAttribute('min')) || 1;
    const max = parseInt(input.getAttribute('max')) || 999;

    if (value < min) {
      input.value = min;
    } else if (value > max) {
      input.value = max;
      this.showNotification(`המקסימום הוא ${max} יחידות`, 'warning');
    }
  }

  animateQuantityChange(input) {
    input.style.transform = 'scale(1.1)';
    input.style.background = 'rgba(102, 126, 234, 0.1)';
    
    setTimeout(() => {
      input.style.transform = 'scale(1)';
      input.style.background = 'transparent';
    }, 200);
  }

  // Cart Updates
  setupCartUpdates() {
    this.debounceTimer = null;
  }

  updateCart() {
    clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => {
      this.performCartUpdate();
    }, 500);
  }

  async performCartUpdate() {
    try {
      this.showLoadingState();
      
      const updates = {};
      document.querySelectorAll('.quantity-input').forEach(input => {
        const index = input.getAttribute('data-index');
        if (index) {
          updates[index] = parseInt(input.value) || 0;
        }
      });

      const response = await fetch(window.Shopify.routes.root + 'cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ updates })
      });

      if (!response.ok) {
        throw new Error('Failed to update cart');
      }

      const cart = await response.json();
      this.updateCartDisplay(cart);
      this.showNotification('העגלה עודכנה בהצלחה', 'success');
      
    } catch (error) {
      console.error('Error updating cart:', error);
      this.showNotification('שגיאה בעדכון העגלה', 'error');
    } finally {
      this.hideLoadingState();
    }
  }

  updateCartDisplay(cart) {
    // Update cart totals
    document.querySelectorAll('[data-cart-total]').forEach(element => {
      element.textContent = this.formatMoney(cart.total_price);
    });

    // Update item counts
    document.querySelectorAll('[data-cart-count]').forEach(element => {
      element.textContent = cart.item_count;
    });

    // Update line item totals
    cart.items.forEach((item, index) => {
      const lineTotal = document.querySelector(`[data-line-total="${index + 1}"]`);
      if (lineTotal) {
        lineTotal.textContent = this.formatMoney(item.final_line_price);
      }
    });
  }

  // Remove Buttons
  setupRemoveButtons() {
    document.querySelectorAll('cart-remove-button').forEach(button => {
      const link = button.querySelector('a');
      if (link) {
        link.addEventListener('click', (e) => {
          e.preventDefault();
          this.confirmRemoveItem(button);
        });
      }
    });
  }

  confirmRemoveItem(button) {
    const cartItem = button.closest('.cart-item');
    const productName = cartItem.querySelector('.cart-item-title').textContent.trim();
    
    if (confirm(`האם אתה בטוח שברצונך להסיר את "${productName}" מהעגלה?`)) {
      this.removeItem(button, cartItem);
    }
  }

  async removeItem(button, cartItem) {
    try {
      this.showLoadingState();
      
      const link = button.querySelector('a');
      const response = await fetch(link.href, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        throw new Error('Failed to remove item');
      }

      // Animate item removal
      cartItem.style.transform = 'translateX(-100%)';
      cartItem.style.opacity = '0';
      
      setTimeout(() => {
        cartItem.remove();
        this.checkEmptyCart();
      }, 300);

      this.showNotification('המוצר הוסר מהעגלה', 'success');
      
    } catch (error) {
      console.error('Error removing item:', error);
      this.showNotification('שגיאה בהסרת המוצר', 'error');
    } finally {
      this.hideLoadingState();
    }
  }

  checkEmptyCart() {
    const cartItems = document.querySelectorAll('.cart-item');
    if (cartItems.length === 0) {
      window.location.reload();
    }
  }

  // Loading States
  setupLoadingStates() {
    this.loadingOverlay = this.createLoadingOverlay();
  }

  createLoadingOverlay() {
    const overlay = document.createElement('div');
    overlay.className = 'loading-overlay';
    overlay.style.display = 'none';
    overlay.innerHTML = '<div class="loading-spinner"></div>';
    return overlay;
  }

  showLoadingState() {
    const cartContainer = document.querySelector('.modern-cart-container');
    if (cartContainer && this.loadingOverlay) {
      cartContainer.style.position = 'relative';
      cartContainer.appendChild(this.loadingOverlay);
      this.loadingOverlay.style.display = 'flex';
    }
  }

  hideLoadingState() {
    if (this.loadingOverlay) {
      this.loadingOverlay.style.display = 'none';
      if (this.loadingOverlay.parentNode) {
        this.loadingOverlay.parentNode.removeChild(this.loadingOverlay);
      }
    }
  }

  // Animations
  setupAnimations() {
    this.observeElements();
    this.setupHoverEffects();
  }

  observeElements() {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.style.animationPlayState = 'running';
        }
      });
    });

    document.querySelectorAll('.cart-item, .cart-summary').forEach(element => {
      observer.observe(element);
    });
  }

  setupHoverEffects() {
    document.querySelectorAll('.cart-item').forEach(item => {
      item.addEventListener('mouseenter', () => {
        this.animateItemHover(item, true);
      });

      item.addEventListener('mouseleave', () => {
        this.animateItemHover(item, false);
      });
    });
  }

  animateItemHover(item, isHover) {
    const image = item.querySelector('.cart-item-image');
    const actions = item.querySelector('.cart-item-actions');
    
    if (isHover) {
      image.style.transform = 'scale(1.05)';
      actions.style.transform = 'translateY(-2px)';
    } else {
      image.style.transform = 'scale(1)';
      actions.style.transform = 'translateY(0)';
    }
  }

  // Accessibility
  setupAccessibility() {
    this.setupKeyboardNavigation();
    this.setupScreenReaderSupport();
  }

  setupKeyboardNavigation() {
    document.querySelectorAll('.quantity-btn').forEach(button => {
      button.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          button.click();
        }
      });
    });
  }

  setupScreenReaderSupport() {
    // Add ARIA labels for better screen reader support
    document.querySelectorAll('.quantity-input').forEach(input => {
      const productName = input.closest('.cart-item').querySelector('.cart-item-title').textContent;
      input.setAttribute('aria-label', `כמות עבור ${productName}`);
    });

    document.querySelectorAll('.remove-btn').forEach(button => {
      const productName = button.closest('.cart-item').querySelector('.cart-item-title').textContent;
      button.setAttribute('aria-label', `הסר ${productName} מהעגלה`);
    });
  }

  // Notifications
  showNotification(message, type = 'info') {
    const notification = this.createNotification(message, type);
    document.body.appendChild(notification);

    setTimeout(() => {
      notification.classList.add('show');
    }, 100);

    setTimeout(() => {
      notification.classList.remove('show');
      setTimeout(() => {
        if (notification.parentNode) {
          notification.parentNode.removeChild(notification);
        }
      }, 300);
    }, 3000);
  }

  createNotification(message, type) {
    const notification = document.createElement('div');
    notification.className = `cart-notification cart-notification--${type}`;
    notification.innerHTML = `
      <div class="cart-notification__content">
        <span class="cart-notification__icon">${this.getNotificationIcon(type)}</span>
        <span class="cart-notification__message">${message}</span>
      </div>
    `;

    // Add notification styles
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: ${this.getNotificationColor(type)};
      color: white;
      padding: 15px 20px;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      transform: translateX(100%);
      transition: transform 0.3s ease;
      z-index: 1000;
      max-width: 300px;
      font-weight: 500;
    `;

    return notification;
  }

  getNotificationIcon(type) {
    const icons = {
      success: '✅',
      error: '❌',
      warning: '⚠️',
      info: 'ℹ️'
    };
    return icons[type] || icons.info;
  }

  getNotificationColor(type) {
    const colors = {
      success: '#38a169',
      error: '#e53e3e',
      warning: '#d69e2e',
      info: '#3182ce'
    };
    return colors[type] || colors.info;
  }

  // Utility Functions
  formatMoney(cents) {
    return new Intl.NumberFormat('he-IL', {
      style: 'currency',
      currency: 'ILS'
    }).format(cents / 100);
  }

  // Auto-save cart state
  saveCartState() {
    const cartState = {
      items: Array.from(document.querySelectorAll('.quantity-input')).map(input => ({
        index: input.getAttribute('data-index'),
        quantity: input.value
      })),
      timestamp: Date.now()
    };
    
    localStorage.setItem('modernCartState', JSON.stringify(cartState));
  }

  restoreCartState() {
    const savedState = localStorage.getItem('modernCartState');
    if (savedState) {
      try {
        const cartState = JSON.parse(savedState);
        // Only restore if saved within last 5 minutes
        if (Date.now() - cartState.timestamp < 300000) {
          cartState.items.forEach(item => {
            const input = document.querySelector(`[data-index="${item.index}"]`);
            if (input && input.value !== item.quantity) {
              input.value = item.quantity;
              this.updateCart();
            }
          });
        }
      } catch (error) {
        console.error('Error restoring cart state:', error);
      }
    }
  }
}

// Add notification styles to head
const notificationStyles = document.createElement('style');
notificationStyles.textContent = `
  .cart-notification.show {
    transform: translateX(0) !important;
  }
  
  .cart-notification__content {
    display: flex;
    align-items: center;
    gap: 10px;
  }
  
  .cart-notification__icon {
    font-size: 16px;
  }
  
  .cart-notification__message {
    font-size: 14px;
    line-height: 1.4;
  }
`;
document.head.appendChild(notificationStyles);

// Initialize Modern Cart when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  window.modernCart = new ModernCart();
  
  // Restore cart state if available
  window.modernCart.restoreCartState();
  
  // Save cart state before page unload
  window.addEventListener('beforeunload', () => {
    window.modernCart.saveCartState();
  });
});

// Handle page visibility changes
document.addEventListener('visibilitychange', () => {
  if (document.hidden) {
    window.modernCart.saveCartState();
  } else {
    window.modernCart.restoreCartState();
  }
});

// Export for external use
window.ModernCart = ModernCart;

